import type { OfferVM, FirmVM } from "./types";
import { effectivePrice } from "./derive";

export interface BestValueSets {
  price: Set<string>;
  total: Set<string>;
  drawdown: Set<string>;
  profitTarget: Set<string>;
  rating: Set<string>;
  roundTripMicro: Set<string>;
  roundTripMini: Set<string>;
  activation: Set<string>;
}

type Dir = "min" | "max";

function winners(
  offers: OfferVM[],
  value: (o: OfferVM) => number | null,
  dir: Dir,
): Set<string> {
  const vals = offers
    .map((o) => ({ id: o.id, v: value(o) }))
    .filter((x): x is { id: string; v: number } => x.v != null);
  if (!vals.length) return new Set();
  const best =
    dir === "min"
      ? Math.min(...vals.map((x) => x.v))
      : Math.max(...vals.map((x) => x.v));
  return new Set(vals.filter((x) => x.v === best).map((x) => x.id));
}

/**
 * For a small set of offers under comparison, compute which offer id(s) hold the
 * "best" value for each criterion (ties include all winners):
 * - price / total / profitTarget / roundTrip / activation → lowest wins
 * - drawdown → largest (most room) wins
 * - rating → highest wins
 */
export function bestOfferIdsByCriterion(
  offers: OfferVM[],
  firms: Map<string, FirmVM>,
  now: Date = new Date(),
): BestValueSets {
  return {
    price: winners(offers, (o) => effectivePrice(o, now), "min"),
    total: winners(offers, (o) => effectivePrice(o, now) + o.activationFeeUsd, "min"),
    drawdown: winners(offers, (o) => o.drawdownUsd, "max"),
    profitTarget: winners(offers, (o) => o.profitTargetUsd, "min"),
    rating: winners(offers, (o) => firms.get(o.firmId)?.rating ?? null, "max"),
    roundTripMicro: winners(offers, (o) => o.roundTripMicroUsd, "min"),
    roundTripMini: winners(offers, (o) => o.roundTripMiniUsd, "min"),
    activation: winners(offers, (o) => o.activationFeeUsd, "min"),
  };
}
